import find from "lodash/find";
import uniqueId from "lodash/uniqueId";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { useTemplate } from "PFApp/hooks";
import { Button } from "PFComponents/button";
import { Typography } from "PFComponents/typography";
import { canEditOrRemoveMatchableTypeValues, useMatchableTypes } from "PFCore/helpers/activities";
import { isRankable } from "PFCore/helpers/custom_type";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import {
  AccessLevel,
  ActivityPureCustomValue,
  CustomType,
  CustomValueScope,
  Id,
  ParsedSchemaProperty
} from "PFTypes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNumber } from "react-use";

import { AttributeTile } from "./attribute_tile";
import css from "./match_attributes.module.scss";
import { canAddNewAttribute, canEditAttribute, canRemoveAttribute } from "./match_attributes.utils";

interface MatchAttributeSectionProps {
  matchableType: ParsedSchemaProperty;
  customType: CustomType;
}

export const MatchAttributeSection = ({ matchableType, customType }: MatchAttributeSectionProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "matchAttributes" });
  const activity = useCurrentActivity();
  const template = useTemplate(activity.template_id);
  const { showPreview } = useCustomValuePreviewActions();
  const { data: currentProfile } = useCurrentProfile();

  const [reopen, { inc: triggerReopen }] = useNumber(0);

  const matchableTypes = useMatchableTypes({
    templateId: activity.template_id,
    levels: [AccessLevel.ReadWrite, AccessLevel.ReadOnly],
    activityCreator: activity.profile
  });

  const isOwner = activity.coowned;
  const { kind } = matchableType;

  const { values } = find(activity.custom_fields, ["type.id", customType.id]) || { values: [] };
  const canAdd = canAddNewAttribute(values, kind, matchableTypes, matchableType);
  const canEdit = isOwner && canEditAttribute(currentProfile, customType, activity.profile);

  const handleMatchAttributeClick = (
    customTypeId: Id,
    customValueId: Id,
    clickedMatchableType: ParsedSchemaProperty,
    keywords: ActivityPureCustomValue[] = []
  ) => {
    const canRemove =
      canRemoveAttribute(keywords, clickedMatchableType, template!) &&
      canEditOrRemoveMatchableTypeValues(clickedMatchableType, matchableTypes);

    showPreview({
      scope: CustomValueScope.Activity,
      customTypeId,
      customValueId,
      activityId: activity.id,
      canEdit: isOwner,
      canRemove
    });
  };

  const handleAddButtonClick = () => {
    showPreview({
      scope: CustomValueScope.Activity,
      customTypeId: customType.id,
      isNew: true,
      allowSearch: true,
      activityId: activity.id,
      canEdit: isOwner,
      key: uniqueId(),
      onReopen: kind === "multiple" ? triggerReopen : undefined
    });
  };

  useEffect(() => {
    reopen && handleAddButtonClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopen]);

  if (!canEdit && values.length === 0) {
    return null;
  }

  return (
    <div>
      <span className={css.header}>
        <Typography variant="h5" title={customType.display_as}>
          {customType.display_as}
        </Typography>
        {canEdit && canAdd && (
          <Button
            aria-label={t("addAriaLabel", { name: customType.name })}
            kind="text"
            text={t("add")}
            onClick={handleAddButtonClick}
          />
        )}
      </span>
      <AttributeTile
        isRankable={isRankable(customType)}
        onAttributeClick={(value) =>
          handleMatchAttributeClick(customType.id, value.id, matchableType, values)
        }
        customType={customType}
        matchKeywords={values}
      />
    </div>
  );
};
